import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ChevronDown, ChevronRight, Users, Activity, ArrowRight,
  Trash2, LogOut, Edit3, Plus, HelpCircle, PowerOff, Loader2,
  UserPlus, UserMinus, RefreshCw, CheckCircle, XCircle, Settings2, Globe, Zap, Lock, Key, LogIn,
  Info, AlertTriangle, AlertCircle, Send, AlertOctagon,
} from "lucide-react";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import { useTheme } from "../../context/ThemeProvider";
import { getColorSchemePalette } from "../../context/colorSchemes";
import { useAuditLog } from "../../api/queryHooks";
import PlotlyChart from "../../components/shared/Viz/PlotlyChart";

// ─── Period tabs (mirrors FinOps) ─────────────────────────────────────────────
const PERIODS = ["7d", "30d", "90d", "Custom"];
const PERIOD_LABEL = { "7d": "last 7 days", "30d": "last 30 days", "90d": "last 90 days" };

// Build a human-readable summary from a raw audit_log record.
const summarize = (e) => {
  const p = e.payload || {};
  const name = p.name || p.title || p.email || p.user_email || p.provider || e.entity_id;
  switch (e.event_type) {
    case "auth.login":                    return "Signed in";
    case "auth.logout":                   return "Signed out";
    case "auth.password_reset_requested": return "Password reset requested";
    case "user.invite_sent":              return `Invited ${p.email || name}`;
    case "user.invite_accepted":          return `${name} accepted invite`;
    case "user.invite_cancelled":         return `Invitation to ${p.email || name} cancelled`;
    case "user.invite_resent":            return `Invite resent to ${p.email || name}`;
    case "user.disabled":                 return `User ${name} disabled`;
    case "user.restored":                 return `User ${name} restored`;
    case "user.purge_initiated":          return `Data purge initiated for ${name}`;
    case "user.role_changed":             return `${name} role → ${p.new_role || "updated"}`;
    case "user.name_updated":             return `Display name updated to "${p.new_name || name}"`;
    case "org.settings_updated":          return "Organisation settings updated";
    case "org.gw_config_set":             return "Gateway config set";
    case "org.gw_config_deleted":         return "Gateway config removed";
    case "org.scim_enabled":              return "SCIM provisioning enabled";
    case "org.scim_token_rotated":        return "SCIM token rotated";
    case "org.scim_disabled":             return "SCIM provisioning disabled";
    case "group.created":                 return `Group "${name}" created`;
    case "group.updated":                 return `Group "${name}" updated`;
    case "group.deleted":                 return `Group "${name}" deleted`;
    case "group.member_added":            return `${p.member_email || p.member_id || name} added to group`;
    case "group.member_removed":          return `${p.member_email || p.member_id || name} removed from group`;
    case "group.policy_updated":          return `Policy updated for group "${name}"`;
    case "group.default_policy_updated":  return "Default group policy updated";
    case "group.gw_registered":           return `Gateway registered for group "${name}"`;
    case "group.synced":                  return `Group "${name}" synced`;
    case "collaborator.invited":          return `${p.email || name} invited as collaborator`;
    case "collaborator.accepted":         return `${p.email || name} accepted collaboration`;
    case "collaborator.removed":          return `Collaborator ${p.email || name} removed`;
    case "approval.granted":              return `Approval granted for ${name}`;
    case "approval.denied":              return `Approval denied for ${name}`;
    case "model.created":                 return `Model "${name}" added`;
    case "model.updated":                 return `Model "${name}" updated`;
    case "model.deleted":                 return `Model "${name}" removed`;
    case "model.defaults_updated":        return "Model defaults updated";
    case "model_provider.updated":        return `Provider "${name}" updated`;
    case "model_provider.deleted":        return `Provider "${name}" removed`;
    case "agent.created":                 return `Forge agent "${name}" created`;
    case "agent.updated":                 return `Forge agent "${name}" updated`;
    case "agent.deleted":                 return `Forge agent "${name}" deleted`;
    case "agent.deletion_blocked":        return `Forge agent "${name}" deletion blocked`;
    case "agent.deletion_failed":         return `Forge agent "${name}" deletion failed`;
    case "provider.created":              return `Forge provider "${name}" created`;
    case "provider.updated":              return `Forge provider "${name}" updated`;
    case "provider.deleted":              return `Forge provider "${name}" deleted`;
    case "provider.credentials_updated":  return `Forge provider "${name}" credentials updated`;
    case "provider.migrated_to_builtin":  return `Forge provider "${name}" migrated to built in`;
    case "provider.deletion_blocked":     return `Forge provider "${name}" deletion blocked`;
    case "provider.deletion_failed":      return `Forge provider "${name}" deletion failed`;
    case "config.updated":               return "Configuration updated";
    case "config.credential_updated":    return `Credential "${name}" updated`;
    case "config.credential_sealed":     return `Credential "${name}" sealed`;
    case "config.credential_revoked":    return `Credential "${name}" revoked`;
    case "skill.updated":                return `Skill "${name}" updated`;
    case "skill.deleted":                return `Skill "${name}" deleted`;
    case "scheduler.job_created":        return `Scheduled job "${name}" created`;
    case "scheduler.job_updated":        return `Scheduled job "${name}" updated`;
    case "scheduler.job_deleted":        return `Scheduled job "${name}" deleted`;
    case "file.deleted":                 return `File "${p.file_name || name}" deleted`;
    case "oauth.connected":              return `${p.provider || name} connected`;
    case "oauth.disconnected":           return `${p.provider || name} disconnected`;
    default:                             return `${e.event_type.replace(/\./g, " › ")}${name ? ` · ${name}` : ""}`;
  }
};

// ─── Prefix-based palette (auto-colors unknown event types by namespace) ───────────
// ─── CRUD + Auth classification ─────────────────────────────────────────────
// Color answers "what kind of thing happened", not "which subsystem did it".
const ACTION_CATEGORY = {
  login: "auth",  logout: "auth",  requested: "auth",
  created: "create", added: "create", invited: "create", sent: "create",
  registered: "create", connected: "create", enabled: "create",
  granted: "create", accepted: "create", provisioned: "create",
  updated: "update", changed: "update", set: "update",
  rotated: "update", synced: "update", resent: "update", sealed: "update",
  deleted: "delete", removed: "delete", cancelled: "delete", disabled: "delete",
  revoked: "delete", initiated: "delete", denied: "delete", disconnected: "delete",
  purged: "delete", deprovisioned: "delete",
  dispatched: "dispatch",
  failed: "fail",
  resolved: "resolve",
  issued: "issue",
};

const CATEGORY_CONFIG = {
  auth:     { accent: "#9333EA", bg: "bg-fill-hex-f5f3ff dark:bg-fill-purple-600/15", text: "text-ink-hex-7e22ce dark:text-ink-hex-c4b5fd" },
  create:   { accent: "#16A34A", bg: "bg-fill-green-50 dark:bg-fill-green-600/15", text: "text-ink-green-700 dark:text-ink-green-300" },
  update:   { accent: "#2563EB", bg: "bg-fill-blue-50 dark:bg-fill-blue-600/15", text: "text-ink-blue-700 dark:text-ink-blue-300" },
  resolve:  { accent: "#38BDF8", bg: "bg-fill-hex-f0f9ff dark:bg-fill-hex-38bdf8/15", text: "text-ink-hex-0369a1 dark:text-ink-hex-7dd3fc" },
  dispatch: { accent: "#6B7280", bg: "bg-fill-gray-50 dark:bg-fill-gray-500/15", text: "text-ink-gray-700 dark:text-ink-gray-300" },
  delete:   { accent: "#18181B", bg: "bg-fill-hex-f4f4f5 dark:bg-fill-hex-18181b/50", text: "text-ink-hex-18181b dark:text-ink-hex-d4d4d8" },
  fail:     { accent: "#F50010", bg: "bg-fill-red-50 dark:bg-fill-hex-f50010/15", text: "text-ink-hex-c00010 dark:text-ink-hex-ff6b6b" },
  issue:    { accent: "#EAB308", bg: "bg-fill-yellow-50 dark:bg-fill-hex-eab308/15", text: "text-ink-yellow-700 dark:text-ink-hex-fde047" },
};

// Action keyword → icon (matched from last segment of event_type)
const ACTION_ICONS = {
  login: LogIn,  logout: LogOut, created: Plus, added: Plus, sent: UserPlus,
  invited: UserPlus, accepted: CheckCircle, connected: CheckCircle, enabled: Zap,
  granted: CheckCircle, registered: Globe, synced: RefreshCw, rotated: RefreshCw,
  resent: RefreshCw, updated: Edit3, changed: Edit3, set: Settings2,
  deleted: Trash2, removed: UserMinus, cancelled: XCircle, disabled: PowerOff,
  revoked: XCircle, initiated: Trash2, denied: XCircle, disconnected: LogOut,
  sealed: Lock, requested: Key, provisioned: UserPlus, deprovisioned: UserMinus,
  purged: Trash2, dispatched: Send, failed: AlertOctagon, issued: Zap,
};

// ─── Event type badge config (explicit overrides; CRUD fallback handles everything else) ──
const EVENT_CONFIG = {};

// Dropdown options — common events shown explicitly; rare ones auto-render via prefix.
const EVENT_TYPE_OPTIONS = [
  { value: "All types",                   label: "All types" },
  { value: "auth.login",                  label: "Login" },
  { value: "auth.logout",                 label: "Logout" },
  { value: "user.invite_sent",            label: "Invite sent" },
  { value: "user.invite_accepted",        label: "Invite accepted" },
  { value: "user.disabled",               label: "User disabled" },
  { value: "user.restored",               label: "User restored" },
  { value: "user.role_changed",           label: "Role changed" },
  { value: "org.settings_updated",        label: "Org settings" },
  { value: "group.created",              label: "Group created" },
  { value: "group.deleted",              label: "Group deleted" },
  { value: "group.member_added",         label: "Member added" },
  { value: "group.member_removed",       label: "Member removed" },
  { value: "group.policy_updated",       label: "Policy updated" },
  { value: "collaborator.invited",       label: "Collaborator invited" },
  { value: "collaborator.removed",       label: "Collaborator removed" },
  { value: "approval.granted",           label: "Approval granted" },
  { value: "approval.denied",            label: "Approval denied" },
  { value: "model.created",              label: "Model added" },
  { value: "model.deleted",              label: "Model removed" },
  { value: "model_provider.updated",     label: "Model provider updated" },
  { value: "model_provider.deleted",     label: "Model provider removed" },
  { value: "agent.created",              label: "Forge agent created" },
  { value: "agent.updated",              label: "Forge agent updated" },
  { value: "agent.deleted",              label: "Forge agent deleted" },
  { value: "agent.deletion_blocked",     label: "Forge agent deletion blocked" },
  { value: "agent.deletion_failed",      label: "Forge agent deletion failed" },
  { value: "provider.created",           label: "Forge provider created" },
  { value: "provider.updated",           label: "Forge provider updated" },
  { value: "provider.deleted",           label: "Forge provider deleted" },
  { value: "provider.credentials_updated", label: "Forge provider credentials updated" },
  { value: "provider.migrated_to_builtin", label: "Forge provider migrated to built in" },
  { value: "provider.deletion_blocked",  label: "Forge provider deletion blocked" },
  { value: "provider.deletion_failed",   label: "Forge provider deletion failed" },
  { value: "config.credential_revoked",  label: "Credential revoked" },
  { value: "file.deleted",              label: "File deleted" },
  { value: "oauth.connected",           label: "OAuth connected" },
  { value: "client.atom_issued",          label: "Atom issued" },
];


// ─── Severity (i / w / e) ───────────────────────────────────────────────────
const SEVERITY = {
  error:   { icon: AlertCircle,   color: "#DC2626", label: "Error" },
  warning: { icon: AlertTriangle, color: "#D97706", label: "Warning" },
  info:    { icon: Info,          color: "#2563EB", label: "Info" },
};

// Map the trailing action word → severity. Everything unlisted is informational.
const SEVERITY_BY_ACTION = {
  failed: "error", denied: "error", revoked: "error",
  deleted: "warning", removed: "warning", disabled: "warning",
  cancelled: "warning", initiated: "warning", disconnected: "warning",
  purged: "warning", deprovisioned: "warning",
};

const getSeverity = (eventType) => {
  const lastWord = eventType?.split(".").pop() ?? "";
  return SEVERITY[SEVERITY_BY_ACTION[lastWord] ?? "info"];
};

const EventBadge = ({ type }) => {
  let cfg = EVENT_CONFIG[type];
  if (!cfg) {
    const action = type?.split(".").slice(1).join("_") ?? "";
    const lastWord = action.split("_").pop();
    const category = ACTION_CATEGORY[lastWord] ?? ACTION_CATEGORY[action];
    const icon = ACTION_ICONS[lastWord] ?? ACTION_ICONS[action] ?? HelpCircle;
    const label = action.replace(/_/g, " ");
    cfg = category
      ? { ...CATEGORY_CONFIG[category], icon, label }
      : { bg: "bg-fill-gray-100 dark:bg-fill-gray-700", text: "text-ink-gray-500 dark:text-ink-gray-400", label: type, icon: HelpCircle };
  }
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

// ─── Expandable payload ───────────────────────────────────────────────────────
const PayloadDrawer = ({ event }) => (
  <motion.div
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: "auto" }}
    exit={{ opacity: 0, height: 0 }}
    transition={{ duration: 0.18 }}
    className="overflow-hidden"
  >
    <div className="px-4 pb-3 pt-2 space-y-2">
      <div className="flex items-center gap-2">
        <EventBadge type={event.event_type} />
        <span className="text-[11px] text-ink-gray-400 font-mono">{event.event_type}</span>
      </div>
      <pre className="text-[11px] leading-relaxed bg-surface-code-block dark:bg-surface-code-block border border-edge-gray-200 dark:border-edge-dialog-surface rounded-lg p-3 overflow-x-auto text-ink-gray-700 dark:text-ink-gray-300">
        {JSON.stringify(event.payload, null, 2)}
      </pre>
    </div>
  </motion.div>
);

// ─── Row ──────────────────────────────────────────────────────────────────────
const EventRow = ({ event }) => {
  const [open, setOpen] = useState(false);
  const ts = new Date(event.timestamp);
  const dateStr = ts.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const timeStr = ts.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  const severity = getSeverity(event.event_type);
  const SeverityIcon = severity.icon;

  return (
    <>
      <tr
        onClick={() => setOpen(o => !o)}
        className="cursor-pointer hover:bg-fill-gray-50 dark:hover:bg-fill-dialog-surface/40 transition-colors border-b border-edge-gray-100 dark:border-edge-dialog-surface last:border-0"
      >
        <td className="px-4 py-3 w-8 text-ink-gray-400">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </td>
        <td className="px-4 py-3 w-10">
          <span title={severity.label} className="inline-flex">
            <SeverityIcon className="w-4 h-4" style={{ color: severity.color }} />
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex flex-col">
            <span className="text-[12px] font-medium text-ink-gray-700 dark:text-ink-gray-300 tabular-nums">{timeStr}</span>
            <span className="text-[11px] text-ink-gray-400">{dateStr}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-col min-w-0 max-w-[160px]" title={event.actor_email || event.actor_id}>
            <span className="text-[12px] text-ink-gray-700 dark:text-ink-gray-300 font-medium truncate">
              {event.actor_id === "system" ? "system" : (event.actor_name || event.actor_email?.split("@")[0] || event.actor_id)}
            </span>
            {event.actor_email && event.actor_id !== "system" && (
              <span className="text-[10px] text-ink-gray-400 truncate">{event.actor_email}</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3"><EventBadge type={event.event_type} /></td>
        <td className="px-4 py-3 text-[12px] text-ink-gray-700 dark:text-ink-gray-300">{event.summary}</td>
      </tr>
      <AnimatePresence>
        {open && (
          <tr>
            <td colSpan={6} className="bg-surface-code-block dark:bg-surface-code-block p-0">
              <PayloadDrawer event={event} />
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
};

// ─── Event histogram ─────────────────────────────────────────────────────────
const HIST_CATS = [
  { key: "fail",     name: "Fail",     color: "#F50010" },
  { key: "issue",    name: "Issue",    color: "#EAB308" },
  { key: "delete",   name: "Delete",   color: "#18181B" },
  { key: "dispatch", name: "Dispatch", color: "#9CA3AF" },
  { key: "resolve",  name: "Resolve",  color: "#38BDF8" },
  { key: "update",   name: "Update",   color: "#2563EB" },
  { key: "create",   name: "Create",   color: "#16A34A" },
  { key: "auth",     name: "Auth",     color: "#9333EA" },
];

const MIN = 60_000, HOUR = 60 * MIN, DAY = 24 * HOUR;
// Candidate bucket sizes from 1 min → 7 days; pick the smallest that yields ≤ ~60 bars.
const BUCKET_SIZES = [MIN, 5 * MIN, 15 * MIN, 30 * MIN, HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY, 7 * DAY];

const pickBucket = (spanMs) => {
  for (const size of BUCKET_SIZES) if (spanMs / size <= 60) return size;
  return BUCKET_SIZES[BUCKET_SIZES.length - 1];
};

const fmtRange = (start, size) => {
  const s = new Date(start);
  const e = new Date(start + size);
  const dateStr = s.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  if (size >= DAY) return dateStr;
  const tz = s.toLocaleTimeString("en-US", { timeZoneName: "short" }).split(" ").pop();
  const t1 = s.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const t2 = e.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${dateStr} · ${t1} – ${t2} ${tz}`;
};

const AuditHistogram = ({ events, selection, onSelect, isDark, palette }) => {
  const { bars, bucketSize } = useMemo(() => {
    const times = events.map(e => new Date(e.timestamp).getTime());
    const min = Math.min(...times), max = Math.max(...times);
    const size = pickBucket(Math.max(max - min, 1));
    const map = {};
    for (const e of events) {
      const t = new Date(e.timestamp).getTime();
      const start = Math.floor(t / size) * size;
      if (!map[start]) map[start] = { auth: 0, create: 0, update: 0, resolve: 0, dispatch: 0, delete: 0, fail: 0, issue: 0, total: 0 };
      const lastWord = e.event_type?.split(".").pop() ?? "";
      const cat = ACTION_CATEGORY[lastWord] ?? "update";
      map[start][cat]++;
      map[start].total++;
    }
    const starts = Object.keys(map).map(Number).sort((a, b) => a - b);
    return { bars: starts.map(s => ({ start: s, ...map[s] })), bucketSize: size };
  }, [events]);

  if (bars.length === 0) return null;

  const centers = bars.map(b => b.start + bucketSize / 2);
  const traces = HIST_CATS.map(c => ({
    x: centers,
    y: bars.map(b => b[c.key]),
    name: c.name,
    type: "bar",
    width: bucketSize * 0.8,
    marker: {
      color: c.color,
      opacity: selection ? bars.map(b => b.start === selection ? 1 : 0.2) : 0.9,
      line: { width: 0 },
    },
    hovertemplate: "<extra></extra>",
  }));

  const minX = bars[0].start - bucketSize * 0.6;
  const maxX = bars[bars.length - 1].start + bucketSize * 1.6;
  const tickFmt = bucketSize < DAY ? "%b %d %H:%M" : "%b %d";

  const layout = {
    barmode: "stack",
    autosize: true,
    height: 150,
    paper_bgcolor: "rgba(0,0,0,0)",
    plot_bgcolor: "rgba(0,0,0,0)",
    margin: { t: 8, r: 8, b: 28, l: 32 },
    showlegend: false,
    hovermode: "x",
    xaxis: {
      type: "date",
      range: [minX, maxX],
      tickformat: tickFmt,
      tickfont: { family: "Inter, sans-serif", size: 10, color: palette.mutedForeground },
      showgrid: false, zeroline: false,
      showline: true,
      linecolor: palette.border,
      ticks: "outside",
      tickcolor: palette.border,
      ticklen: 4,
      nticks: Math.min(bars.length + 1, 8),
      showspikes: false,
    },
    yaxis: {
      tickfont: { family: "Inter, sans-serif", size: 10, color: palette.mutedForeground },
      showgrid: true,
      gridcolor: palette.border,
      zeroline: false,
      nticks: 4,
      rangemode: "tozero",
      showspikes: false,
    },
  };

  const handleHover = (d) => {
    if (!d.points?.length) return null;
    const ptIdx = d.points[0].pointIndex;
    const bar = bars[ptIdx];
    if (!bar) return null;
    return {
      label: fmtRange(bar.start, bucketSize),
      total: bar.total,
      active: HIST_CATS.filter(c => bar[c.key] > 0).map(c => ({ ...c, count: bar[c.key] })),
    };
  };

  return (
    <div>
      <style>{`.audit-hist .hoverlayer { display: none; }`}</style>
      {/* Legend — right-aligned row above chart */}
      <div className="flex justify-end items-center gap-3 px-1 pb-1.5 pointer-events-none">
        {HIST_CATS.slice().reverse().map(({ color, name }) => (
          <span key={name} className="flex items-center gap-1 text-[10px] text-ink-gray-400">
            <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
            {name}
          </span>
        ))}
      </div>
      <PlotlyChart
        className="audit-hist"
        data={traces}
        layout={layout}
        config={{ responsive: true, scrollZoom: true, displayModeBar: "hover", modeBarButtonsToRemove: ["toImage", "sendDataToCloud", "toggleSpikelines", "hoverClosestCartesian", "hoverCompareCartesian"] }}
        isDark={isDark}
        onHoverData={handleHover}
        renderTooltip={(payload, dark) => (
          <div className="p-3">
            <div className="text-[10px] font-medium text-ink-gray-400 mb-2">{payload.label}</div>
            {payload.active.map(({ name, color, count }) => (
              <div key={name} className="flex items-center gap-2 py-0.5">
                <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                <span className="flex-1">{name}</span>
                <span className={`tabular-nums font-semibold ml-4 ${dark ? "text-ink-white" : "text-ink-gray-900"}`}>{count}</span>
              </div>
            ))}
            <div className={`border-t mt-2 pt-1.5 flex justify-between ${dark ? "border-edge-dialog-surface" : "border-edge-gray-200"}`}>
              <span className="text-ink-gray-400">Total</span>
              <span className={`font-bold tabular-nums ${dark ? "text-ink-white" : "text-ink-gray-900"}`}>{payload.total}</span>
            </div>
          </div>
        )}
        tooltipWidth={200}
        onClick={(d) => {
          if (!d.points?.length) return;
          const center = new Date(d.points[0].x).getTime();
          const start = Math.floor(center / bucketSize) * bucketSize;
          onSelect(selection === start ? null : start, bucketSize);
        }}
        style={{ width: "100%", cursor: "pointer" }}
        useResizeHandler
      />
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const AuditLogPage = () => {
  const { theme, colorScheme } = useTheme();
  const palette = getColorSchemePalette(colorScheme, theme);
  const [period, setPeriod] = useState("7d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("All types");
  const [search, setSearch] = useState("");
  const [histSelection, setHistSelection] = useState(null);

  // Clear histogram selection when the query window changes.
  useEffect(() => { setHistSelection(null); }, [period, eventTypeFilter, customFrom, customTo]);

  const customReady = period !== "Custom" || (!!customFrom && !!customTo);

  // Query params sent to the backend — period, custom dates, server-side event_type.
  const queryParams = useMemo(() => {
    const p = { limit: 500 };
    if (period !== "Custom") {
      p.period = period.toLowerCase();
    } else if (customFrom && customTo) {
      p.period = "custom";
      p.from = customFrom;
      p.to = customTo;
    }
    if (eventTypeFilter !== "All types") p.eventType = eventTypeFilter;
    return p;
  }, [period, customFrom, customTo, eventTypeFilter]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useAuditLog(queryParams, { enabled: customReady });

  // Flatten pages and map to row shape.
  const allEvents = useMemo(() =>
    (data?.pages ?? []).flatMap(page =>
      (page.events ?? []).map(e => ({
        id: e.event_id,
        timestamp: e.timestamp,
        actor_id: e.actor_id || "system",
        actor_name: e.actor_name || null,
        actor_email: e.actor_email || null,
        entity_type: e.entity_type,
        entity_id: e.entity_id,
        event_type: e.event_type,
        summary: summarize(e),
        payload: e.payload || {},
      }))
    ),
  [data]);

  // Client-side text search + histogram date selection.
  const filtered = useMemo(() => {
    let out = allEvents;
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(e =>
        [e.actor_name, e.actor_email, e.actor_id, e.event_type, e.summary, e.entity_id]
          .some(s => s?.toLowerCase().includes(q))
      );
    }
    if (histSelection) {
      const { start, size } = histSelection;
      out = out.filter(e => {
        const t = new Date(e.timestamp).getTime();
        return t >= start && t < start + size;
      });
    }
    return out;
  }, [allEvents, search, histSelection]);

  const periodLabel = period !== "Custom"
    ? PERIOD_LABEL[period]
    : (customFrom && customTo ? `${customFrom} – ${customTo}` : "");

  return (
    <SettingsPageShell
      title="Audit Log"
      description="Immutable record of all admin and collaboration actions across your organisation."
      maxWidth="6xl"
    >
      {/* Period selector */}
      <div className="flex items-center gap-1 bg-surface-muted dark:bg-surface-muted rounded-lg p-1 w-fit mb-3">
        {PERIODS.map(p => (
          <button
            key={p}
            onClick={() => { setPeriod(p); setCustomFrom(""); setCustomTo(""); }}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              period === p
                ? "bg-fill-white dark:bg-fill-deep-surface text-ink-primary dark:text-ink-accent shadow-sm"
                : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-900 dark:hover:text-ink-white"
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Custom date range — slides in when Custom selected */}
      <AnimatePresence>
        {period === "Custom" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden mb-4"
          >
            <div className="flex items-center gap-2 pt-3 pb-1">
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400">From</span>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  max={customTo || undefined}
                  className="w-36 text-xs !border-edge-gray-200 dark:!border-edge-dialog-border [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-ink-gray-400 mt-4 flex-shrink-0" />
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400">To</span>
                <Input
                  type="date"
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  min={customFrom || undefined}
                  className="w-36 text-xs !border-edge-gray-200 dark:!border-edge-dialog-border [color-scheme:light] dark:[color-scheme:dark]"
                />
              </div>
              {customFrom && customTo && (
                <button
                  onClick={() => { setCustomFrom(""); setCustomTo(""); }}
                  className="mt-4 text-[11px] text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-primary dark:hover:text-ink-accent transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary bar */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-fill-blue-50 dark:bg-fill-primary/10 border border-edge-blue-200 dark:border-edge-primary/30 rounded-lg px-3 py-2">
          <Activity className="w-3.5 h-3.5 text-ink-primary dark:text-ink-blue-300" />
          <span className="text-[11px] text-ink-blue-700 dark:text-ink-blue-300 font-medium">Events</span>
          <span className="text-[13px] font-bold text-ink-blue-700 dark:text-ink-blue-300 tabular-nums">{allEvents.length}</span>
        </div>
        <div className="flex items-center gap-2 bg-fill-hex-f5f3ff dark:bg-fill-violet-600/10 border border-edge-hex-ddd6fe dark:border-edge-violet-600/30 rounded-lg px-3 py-2">
          <Users className="w-3.5 h-3.5 text-ink-violet-600 dark:text-ink-hex-c4b5fd" />
          <span className="text-[11px] text-ink-hex-6d28d9 dark:text-ink-hex-c4b5fd font-medium">Actors</span>
          <span className="text-[13px] font-bold text-ink-hex-6d28d9 dark:text-ink-hex-c4b5fd tabular-nums">{new Set(allEvents.map(e => e.actor_id)).size}</span>
        </div>
        <div className="flex items-center gap-2 bg-fill-red-50 dark:bg-fill-red-600/10 border border-edge-red-200 dark:border-edge-red-600/30 rounded-lg px-3 py-2">
          <Trash2 className="w-3.5 h-3.5 text-ink-red-600 dark:text-ink-red-300" />
          <span className="text-[11px] text-ink-red-600 dark:text-ink-red-300 font-medium">Deletions</span>
          <span className="text-[13px] font-bold text-ink-red-600 dark:text-ink-red-300 tabular-nums">{allEvents.filter(e => e.event_type.includes("deleted") || e.event_type.includes("decommissioned")).length}</span>
        </div>
        {periodLabel && (
          <span className="text-[11px] text-ink-gray-400 ml-2">{periodLabel}</span>
        )}
      </div>

      {/* Histogram */}
      {allEvents.length > 0 && (
        <div className="mb-1">
          <AuditHistogram
            events={allEvents}
            selection={histSelection?.start ?? null}
            onSelect={(start, size) => setHistSelection(start === null ? null : { start, size })}
            isDark={theme === "dark"}
            palette={palette}
          />
          <div className="flex items-center gap-3 mb-3 px-0.5">
            {histSelection && (
              <button
                onClick={() => setHistSelection(null)}
                className="text-[10px] text-ink-primary dark:text-ink-accent hover:underline"
              >
                Clear · {fmtRange(histSelection.start, histSelection.size)}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filters row */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-gray-400 z-10" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search actor, event, entity…"
            className="pl-8"
          />
        </div>
        <div className="w-52">
          <Select
            value={eventTypeFilter}
            onChange={setEventTypeFilter}
            options={EVENT_TYPE_OPTIONS}
          />
        </div>
        {(search || eventTypeFilter !== "All types") && (
          <button
            onClick={() => { setSearch(""); setEventTypeFilter("All types"); }}
            className="text-xs text-ink-primary dark:text-ink-accent hover:underline"
          >
            Clear filters
          </button>
        )}
        <span className="ml-auto text-[11px] text-ink-gray-400">{filtered.length} event{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <div className="bg-surface-card-deep dark:bg-surface-card-deep border border-edge-gray-200 dark:border-edge-dialog-surface rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-edge-gray-200 dark:border-edge-dialog-surface bg-surface-table-header dark:bg-surface-table-header">
              {["", "Severity", "Time", "Actor", "Event", "Summary"].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <AnimatePresence initial={false}>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-ink-gray-400">
                    {isLoading
                      ? "Loading events…"
                      : isError
                        ? "Couldn't load audit events."
                        : period === "Custom" && !customReady
                          ? "Select a date range to load events."
                          : "No events in this window."}
                  </td>
                </tr>
              ) : (
                filtered.map(event => <EventRow key={event.id} event={event} />)
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {hasNextPage && (
        <div className="flex justify-center mt-4">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-fill-gray-100 dark:bg-fill-dialog-surface text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-200 dark:hover:bg-fill-gray-700 transition-colors disabled:opacity-50"
          >
            {isFetchingNextPage && <Loader2 className="w-3 h-3 animate-spin" />}
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default AuditLogPage;
